import { isEqual as _isEqual } from 'underscore';

import { cloneDeep } from 'lodash';

import { EngagementEventTypeEnum } from '../../../api/mentions/enum/engagement-event-type.enum';
import { EngagementAgeEnum } from '../../../api/mentions/enum/engagement-age.enum';
import { MediaTypeEnum } from '../../Basic/enums/media-type.enum';
import { LinkedinSubTypeEnum } from '../../Basic/enums/linkedin-sub-type.enum';
import { TVEyesArchivedStateEnum } from '../../../api/mentions/enum/tv-eyes-archived-state.enum';
import { TwitterSubTypeEnum } from '../../Basic/enums/twitter-sub-type.enum';
import { CreateMentionApiModel, createMentionApiProperties } from '../../../api/mentions/model/create-mention-api.model';
import { EngagementDataModel, EngagementValue } from '../../../api/mentions/model/engagement-data.model';
import { ImageMetadataApiModel } from '../../../api/mentions/model/image-metadata.api.model';
import { MentionSocialDataModel } from '../../../api/mentions/model/mention-social-data.model';
import { MentionModel, PartialMentionApiModel } from '../../../api/mentions/model/mention.model';
import { LicenseSummaryModel } from '../../../api/mentions/model/mention-license-summery.model';
import { TweetDataModel } from '../../../api/mentions/model/tweet-data.model';
import { RedditDataModel } from '../../../api/mentions/model/reddit-data.model';
import { YoutubeDataModel } from '../../../api/mentions/model/youtube-data.model';
import { TiktokDataModel } from '../../../api/mentions/model/tiktok-data.model';
import { BlueskyData } from '../../../api/mentions/model/bluesky-data.model';
import { Author } from '../../../Monitor/Shared/models/author';
import { MediaTypeModel } from '../../../Monitor/Shared/models/media-type.model';
import { ProminenceScoreModel } from '../../../Monitor/Shared/models/prominence-score.model';
import { SyndicateInfoModel } from '../../../Monitor/Shared/models/syndicate-info.model';
import { Publication } from '../../../Monitor/Shared/models/publication';
import { basicClone, Clonable } from '../../Basic/decorators/clone.decorator';
import { webVideoHelper } from '../../Misc/helpers/webVideo/webVideo.helper';
import { ContentSharingEmailModel } from '../../Misc/models/content-sharing-email.model';
import { ContentSharingFacebookModel } from '../../Misc/models/content-sharing-facebook.model';
import { ContentSharingLinkedinModel } from '../../Misc/models/content-sharing-linkedin.model';
import { ContentSharingTweetModel } from '../../Misc/models/content-sharing-tweet.model';
import { ContentSharingModel } from '../../Misc/models/content-sharing.model';
import { ImageModel } from '../../Misc/models/image.model';
import { KeywordLocationModel } from './keyword-location.model';
import { LanguageModel } from '../../../Core/Localization/models/language.model';
import { MentionInsightModel } from './mention-insight.model';
import { HandlePageTypeEnum, LinkedinDataModel } from '../../../api/mentions/model/linkedin-data-model';
import { ViewerUrlTypeEnum } from '../enums/viewer-url-type.enum';

export interface TranslatedMention {
    language?: string; // ex: en
    languageString?: string; // ex: en
    headline?: string;
    headlineHighlightIndexes?: KeywordLocationModel[];
    previousBody?: string;
    body?: string;
    bodyHighlightIndexes?: KeywordLocationModel[];
    summary?: string;
    summaryHighlightIndexes?: KeywordLocationModel[];
    lead?: string;
    paragraphs?: string[];
    summaryParagraphs?: string[];
    failedToTranslate?: boolean;
    textDirection?: string;
};

export class Mention {
    /** Maximum Audience reach (impressions) value (1 trillion) */
    public static readonly maxAudienceReach = 999999999999;
    /** Maximum AVE value (1 trillion) */
    public static readonly maxAVE = 999999999999;
    /** Maximum Engagement Rate value (1 trillion) */
    public static readonly maxEngagementRate = 999999999999;
    /** Maximum Engagement Total value (1 trillion) */
    public static readonly maxEngagementTotal = 999999999999;
    /** Maximum word count value (1 Million) */
    public static readonly maxWordCount = 999999;
    /** Maximum headline character count value (2048) */
    public static readonly maxHeadlineCharCount = 2048;
    /** Maximum word count value (100) */
    public static readonly maxDomainAuthorityScore = 100;
    // needed to show slim player for legacy archived clips
    // multiple domains because mercury may point to cm preprod at times
    public static readonly criticalMentionDomains: string[] = ['https://api.criticalmention.com/', 'https://staging-partner.criticalmention.com'];

    // (frontend) id === (backend) mercuryId
    @Clonable()
    public id: string;

    // (frontend) mentionId === (backend) id
    @Clonable()
    public mentionId: string;

    @Clonable()
    public audienceReach: number;
    @Clonable()
    public isAudienceFromSimilarWeb: boolean;
    @Clonable()
    public isLexisNexis: boolean;
    @Clonable()
    public isFinancialTimes: boolean;
    @Clonable()
    public isBurrelles: boolean;
    @Clonable()
    public isPmgXcago: boolean;
    @Clonable()
    public isNla: boolean;
    @Clonable()
    public isCla: boolean;
    @Clonable()
    public isRetweet: boolean;
    @Clonable()
    public isTweetReply: boolean;
    @Clonable()
    public isArchivedCriticalMention: boolean;
    @Clonable()
    public isArchivedMention: boolean;
    @Clonable()
    public author: Author;
    @Clonable()
    public adValueUsd: number;
    @Clonable()
    public body: string;
    @Clonable()
    public dateAvailable: Date;
    @Clonable()
    public datePublished: Date;
    @Clonable()
    public expiryDate: Date;
    @Clonable()
    public headline: string;
    @Clonable()
    public images: ImageModel[];
    @Clonable()
    public language: string; // 2 character iso code
    @Clonable()
    public mediaType: MediaTypeModel;
    @Clonable()
    public publication: Publication;
    @Clonable()
    public sentiment: number;
    @Clonable()
    public sentimentName: string;
    @Clonable()
    public url: string;
    @Clonable()
    public urlRemoved: boolean;
    @Clonable()
    public folderIds: number[];
    @Clonable()
    public createdByUserId: number;
    @Clonable()
    public createdDate: Date;
    @Clonable()
    public lastModifiedByUserId: number;
    @Clonable()
    public lastModifiedDate: Date;
    @Clonable()
    public tagIds: number[];
    @Clonable()
    public viewerUrl: string;
    @Clonable()
    public viewerUrlType: ViewerUrlTypeEnum;
    @Clonable()
    public summary: string;
    @Clonable()
    public note: string;
    @Clonable()
    public wordCount: number;
    @Clonable()
    public duration: number;

    @Clonable()
    public cityId: number;
    @Clonable()
    public continentId: number;
    @Clonable()
    public countryId: number;
    @Clonable()
    public countyId: number;
    @Clonable()
    public provinceId: number;

    @Clonable()
    public contexualKeywordIndices: KeywordLocationModel[];
    @Clonable()
    public summaryContextualKeywordIndices: KeywordLocationModel[];
    @Clonable()
    public duplicateGroupId: string;
    @Clonable()
    public syndicateInfo?: SyndicateInfoModel;
    @Clonable()
    public criticalMentionWordPlayUrl: string;

    @Clonable()
    public useSummaryForDisplay: boolean = false;
    @Clonable()
    public tvEyesArchiveState: TVEyesArchivedStateEnum;
    @Clonable()
    public tvEyesPodcastID: string;
    @Clonable()
    public tvEyesInstaPlayUrl: string;

    @Clonable()
    public thumbnailUrl: string;

    @Clonable()
    public attachments: string[] = [];

    @Clonable()
    public cachedUrl: string;

    @Clonable()
    public domainAuthority: number;

    /** Stores the searched keywords */
    @Clonable()
    public contextualKeywords: string[];
    @Clonable()
    public attribution: string;

    @Clonable()
    public licenseSummary: LicenseSummaryModel;

    @Clonable()
    translation?: TranslatedMention;

    @Clonable()
    isTranslatedPreferred?: boolean;

    @Clonable()
    isEdited: boolean;

    @Clonable()
    isBroadcastAdvertisement?: boolean;

    insights: MentionInsightModel[];
    expired: boolean;

    canGenerateInsight: boolean;

    showTranslated?: boolean;
    translating?: boolean;

    // mention csv import used only
    cityName?: string;
    stateName?: string;
    countryName?: string;
    originalUrl?: string;

    // For influencer streams
    public imagesExpanded: boolean = false;
    public avatarUrl: string;
    public twitterHandle: string;
    public youtubeHandle: string;
    public blueskyHandle: string;
    // "X minutes ago" style
    // TODO : populate
    public relativeDateString: string;

    public prominenceScores: ProminenceScoreModel[];

    public engagementLastFetchedDate: Date;

    public view: number = null;
    public like: number = null;
    public share: number = null;
    public reply: number = null;
    public quote: number = null;

    /** saved per engagement type, but should be treated as a global value across all engagements */
    public engagementAge: EngagementAgeEnum;

    public username: string;
    public handle: string;
    public tweetData: TweetDataModel;
    public redditData: RedditDataModel;
    public youtubeData: YoutubeDataModel;
    public tiktokData: TiktokDataModel;
    public linkedinData: LinkedinDataModel;
    public blueskyData: BlueskyData;

    public engagements: number = 0;
    public engagementRate: number = 0;
    public socialAvatarUrl: string;
    public socialUrl: string;
    public avatarPlaceholderText: string;
    public subreddit: string;
    public musicTitle: string;
    public musicUrl: string;

    private patchSimpleCompare: string[] = [
        'accountId',
        'agilityContactId',
        'agilityOutletId',
        'audienceReach',
        'adValueUsd',
        'body',
        'cachedUrl',
        'cityId',
        'contactId',
        'contactName',
        'continentId',
        'countryId',
        'countyId',
        'duplicateGroupId',
        'harvestedDate',
        'expiryDate',
        'language',
        'mediaType',
        'mercuryId',
        'outletId',
        'outletName',
        'providerUrl',
        'provinceId',
        'publishedDate',
        'sentiment',
        'summary',
        'note',
        'title',
        'url',
        'userId',
        'criticalMentionWordPlayUrl',
        'wordCount',
        'tveyesArchivedState',
        'tvEyesInstaPlayUrl',
        'clipDuration',
        'isEdited',
        'isBroadcastAdvertisement'
    ];

    // For mention sharing
    shareable: boolean = false;
    sharingInformation: ContentSharingModel;

    languageModel: LanguageModel;

    /** Stores the saved keywords */
    matchedKeywords: string[];

    // F/E only
    selected: boolean = false;

    /**
     * Constructs a Mention object, possibly by importing data from the MentionsApi
     *
     * @param apiMention Optional. A Mention from the MentionsApi as a data-source
     */
    constructor(apiMention?: MentionModel) {
        if (apiMention) {
            this.id = apiMention.mercuryId;
            this.mentionId = apiMention.id;
            this.folderIds = apiMention.folderIds;
            this.datePublished = this.convertDate(apiMention.publishedDate);
            this.dateAvailable = this.convertDate(apiMention.harvestedDate);
            this.author = {
                id: apiMention.contactId,
                uuid: apiMention.contactUuid,
                contactUrl: apiMention.contactId ? '/Contact/ContactProfile?contactid=' + apiMention.contactId : null,
                agilityContactId: apiMention.agilityContactId,
                agilityContactURL: apiMention.agilityContactId ? '/Contact/ContactProfile?contactid=' + apiMention.agilityContactId : null,
                name: apiMention.contactName
            } as Author;
            this.body = apiMention.body ? apiMention.body : '';
            this.headline = apiMention.title;
            if (apiMention.providerUrl) {
                this.url = apiMention.providerUrl;
            }
            if (apiMention.url) {
                this.url = apiMention.url;
            }
            this.urlRemoved = apiMention.urlRemoved;
            this.publication = {
                id: apiMention.outletId,
                outletUrl: apiMention.outletId ? '/Outlets/outletprofile?outletID=' + apiMention.outletId : null,
                agilityOutletId: apiMention.agilityOutletId,
                agilityOutletURL: apiMention.agilityOutletId ? '/Outlets/outletprofile?outletID=' + apiMention.agilityOutletId : null,
                name: apiMention.outletName,
                uuid: apiMention.outletUuid
            } as Publication;
            this.images = this.getImagesFromApiEntity(apiMention);
            this.language = apiMention.language;
            this.languageModel = new LanguageModel(apiMention.language);
            this.mediaType = new MediaTypeModel(apiMention.mediaType);
            this.sentiment = apiMention.sentiment === undefined ? null : apiMention.sentiment;
            this.sentimentName = this.getSentimentName(this.sentiment);
            this.audienceReach = apiMention.audienceReach === undefined ? null : apiMention.audienceReach;
            this.isAudienceFromSimilarWeb = apiMention.isAudienceFromSimilarWeb;
            this.isLexisNexis = apiMention.isLexisNexis;
            this.isBurrelles = apiMention.isBurrelles;
            this.isPmgXcago = apiMention.isPmgXcago;
            this.isCla = apiMention.isCla;
            this.isNla = apiMention.isNla;
            this.isRetweet = apiMention.isRetweet;
            this.isTweetReply = apiMention.isTweetReply;
            this.isFinancialTimes = apiMention.isFinancialTimes;
            this.attribution = apiMention.attribution;
            this.isArchivedCriticalMention = apiMention.isArchivedCriticalMention;
            this.isArchivedMention = this.isMentionArchived(apiMention);
            this.adValueUsd = apiMention.adValueUsd === undefined ? null : apiMention.adValueUsd;
            this.createdDate = this.convertDate(apiMention.createdDate);
            this.createdByUserId = apiMention.createdByUserId;
            this.lastModifiedDate = this.convertDate(apiMention.lastModifiedDate);
            this.lastModifiedByUserId = apiMention.lastModifiedByUserId;
            this.cityId = apiMention.cityId ? +apiMention.cityId : null;
            this.continentId = apiMention.continentId ? +apiMention.continentId : null;
            this.countryId = apiMention.countryId ? +apiMention.countryId : null;
            this.countyId = apiMention.countyId ? +apiMention.countyId : null;
            this.provinceId = apiMention.provinceId ? +apiMention.provinceId : null;
            this.contexualKeywordIndices = [];
            this.summaryContextualKeywordIndices = [];
            this.criticalMentionWordPlayUrl = apiMention.criticalMentionWordPlayUrl;
            this.licenseSummary = apiMention?.licenseSummary ? new LicenseSummaryModel(apiMention.licenseSummary, apiMention?.mercuryId) : null;
            if (apiMention.contexualKeywordIndices) {
                this.contexualKeywordIndices = apiMention.contexualKeywordIndices.map(indices => new KeywordLocationModel(indices));
            }
            if (apiMention.summaryContextualKeywordIndices) {
                this.summaryContextualKeywordIndices = apiMention.summaryContextualKeywordIndices.map(indices => new KeywordLocationModel(indices));
            }
            if (apiMention.tagIds) {
                this.tagIds = apiMention.tagIds;
            }
            this.duplicateGroupId = apiMention.duplicateGroupId;
            this.domainAuthority = apiMention.domainAuthority;
            this.viewerUrl = apiMention.viewerUrl;
            if (apiMention.viewerUrlType) {
                if (apiMention.viewerUrlType === ViewerUrlTypeEnum.PDF) {
                    this.viewerUrlType = ViewerUrlTypeEnum.PDF;

                    if (!this.url) {
                        this.url = this.viewerUrl;
                    }
                }
            }
            this.summary = apiMention.summary;
            this.note = apiMention.note;
            this.wordCount = apiMention.wordCount === undefined ? null : apiMention.wordCount;
            this.tvEyesArchiveState = apiMention.tveyesArchivedState;
            this.tvEyesPodcastID = this.getPodcastId(apiMention);
            this.tvEyesInstaPlayUrl = apiMention.tvEyesInstaPlayUrl;
            this.duration = apiMention.clipDuration;
            this.attachments = apiMention.attachmentMediaIds;
            this.thumbnailUrl = apiMention.thumbnailUrl;
            this.cachedUrl = apiMention.cachedUrl;
            this.expiryDate = this.convertDate(apiMention.expiryDate);
            this.expired = this.expiryDate < new Date();
            this.prominenceScores = !!apiMention.prominenceScores ? apiMention.prominenceScores.map(x => new ProminenceScoreModel(x)) : null;
            this.isBroadcastAdvertisement = apiMention.isBroadcastAdvertisement;
            this.setMentionSharing();
            if (apiMention.insights?.length) {
                this.insights = [];
                apiMention.insights.forEach(insight => this.insights.push(new MentionInsightModel(insight)));
                this.insights.sort((a, b) => {
                    if (a.isPreferred) {
                        return -1;
                    }

                    if (b.isPreferred) {
                        return 1;
                    }
                    return 0;
                });
            }

            this.contextualKeywords = [];

            apiMention.contexualKeywordIndices?.forEach(index => {
                const keyword = apiMention.body.substring(index.start, index.end);
                if (!this.contextualKeywords.some(item => item.toLowerCase() === keyword.toLowerCase())) {
                    this.contextualKeywords.push(keyword);
                }
            });

            this.matchedKeywords = apiMention.matchedKeywords;

            apiMention.summaryContextualKeywordIndices?.forEach(index => {
                const keyword = apiMention.summary.substring(index.start, index.end);
                if (!this.contextualKeywords.some(item => item.toLowerCase() === keyword.toLowerCase())) {
                    this.contextualKeywords.push(keyword);
                }
            });

            this.useSummaryForDisplay = !!apiMention.summary;

            if (apiMention.socialData) {
                this.view = this.getEngagementValueByType(EngagementEventTypeEnum.view, apiMention.socialData.engagementData);
                this.like = this.getEngagementValueByType(EngagementEventTypeEnum.like, apiMention.socialData.engagementData);
                this.share = this.getEngagementValueByType(EngagementEventTypeEnum.share, apiMention.socialData.engagementData);
                this.reply = this.getEngagementValueByType(EngagementEventTypeEnum.reply, apiMention.socialData.engagementData);
                this.quote = this.getEngagementValueByType(EngagementEventTypeEnum.quote, apiMention.socialData.engagementData);

                this.engagementAge = this.getEngagementAge(apiMention.socialData.engagementData);

                this.username = apiMention.socialData.username;
                this.tweetData = apiMention.socialData.tweetData;
                this.redditData = apiMention.socialData.redditData;
                this.youtubeData = apiMention.socialData.youtubeData;
                this.tiktokData = apiMention.socialData.tiktokData;
                this.linkedinData = apiMention.socialData.linkedinData;
                this.blueskyData = apiMention.socialData.blueskyData;

                if (this.mediaType.id === MediaTypeEnum.YOUTUBE) {
                    this.youtubeHandle = apiMention.socialData?.youtubeData?.channelUrl?.match(/@[^/]+/)?.[0];
                }

                this.avatarPlaceholderText = this.getAvatarPlaceholderText(apiMention.socialData.handle);

                if (apiMention.socialData.handle) {
                    this.handle = this.processHandle(apiMention.socialData.handle);
                }

                this.engagementLastFetchedDate = apiMention.socialData.engagementLastFetchedDate
                    ? this.convertDate(apiMention.socialData.engagementLastFetchedDate) : undefined;

                this.engagements = this.like + this.share + this.reply + this.quote;
                if (this.audienceReach) {
                    this.engagementRate = +(this.engagements / this.audienceReach * 100).toFixed(6);
                }
                this.socialAvatarUrl = apiMention.socialData.avatarUrl;

                // For reddit, if b/e fails to return avatarUrl, we rely on unavatar
                if (!this.socialAvatarUrl && this.mediaType.id === MediaTypeEnum.REDDIT) {
                    this.socialAvatarUrl = 'https://unavatar.io/reddit/' + apiMention.socialData.username + '?fallback=false';
                }

                if (apiMention.socialData?.redditData?.subreddit) {
                    this.subreddit = 'r/' + apiMention.socialData.redditData.subreddit;
                }

                if (apiMention.socialData?.tiktokData) {
                    this.musicTitle = apiMention.socialData?.tiktokData?.musicTitle + ' - ' + apiMention.socialData?.tiktokData?.musicAuthor;
                    this.musicUrl = apiMention.socialData?.tiktokData?.musicUrl;
                }
                this.prepareSocialUrl(apiMention.socialData);
            }

            this.isTranslatedPreferred = apiMention.isTranslatedPreferred;
            this.isEdited = apiMention.isEdited;
            if (apiMention.translation) {
                this.translation = apiMention.translation;
                this.translation.paragraphs = (this?.translation?.body || '').split('\n\n');
                this.translation.summaryParagraphs = (this?.translation?.summary || '').split('\n\n');
            }

            this.canGenerateInsight = apiMention.canGenerateInsight;
        } else {
            // create some empty defaults
            this.publication = new Publication();
            this.author = new Author();
            this.folderIds = [];
            this.images = [];
            this.matchedKeywords = [];
        }

        // Handle xcago viewerUrl as url
        if (
            this.isPmgXcago &&
            !this.url &&
            this.viewerUrl
        ) {
            this.url = this.viewerUrl;
        }
    }

    /**
    * Checks if a mention source is CriticalMention
    */
    public static isCriticalMention(mention: Mention): boolean {
        const isTV: boolean = mention.mediaType.id === MediaTypeEnum.TV;
        const isRadio: boolean = mention.mediaType.id === MediaTypeEnum.RADIO;
        let isCriticalMentionURL: boolean;

        if (mention.criticalMentionWordPlayUrl) {
            isCriticalMentionURL = true;
        } else if (mention.url) {
            // needed to manage legacy archived that can not be parsed to mention.criticalMentionWordPlayUrl
            isCriticalMentionURL =  !!Mention.criticalMentionDomains.find(criticalMentionUrl => mention.url.includes(criticalMentionUrl));
        }

        return (isTV || isRadio) && isCriticalMentionURL;
    }

    /**
     * Converts the Mention object to a MentionModel which can be used by MentionsApi
     *
     * @returns A Mention object ready to be used with the MentionsApi
     */
    public convertToApiMention(): MentionModel {
        const apiMention: MentionModel = new MentionModel();
        apiMention.id = this.mentionId;
        apiMention.folderIds = this.folderIds;
        apiMention.mercuryId = this.id;
        apiMention.publishedDate = +this.datePublished;
        apiMention.harvestedDate = +this.dateAvailable;
        if (this.author) {
            apiMention.contactName = this.author.name;
            apiMention.agilityContactId = this.author.agilityContactId;
            apiMention.contactId = this.author.id;
        }
        apiMention.body = this.body;
        apiMention.title = this.headline;
        apiMention.url = this.url;
        if (this.publication) {
            apiMention.outletId = this.publication.id;
            apiMention.agilityOutletId = this.publication.agilityOutletId;
            apiMention.outletName = this.publication.name;
            apiMention.outletUuid = this.publication.uuid;
        }
        apiMention.domainAuthority = this.domainAuthority;
        apiMention.images = (this.images || []).map((image) => image.toApiEntity());
        apiMention.language = this.language;
        apiMention.mediaType = this.mediaType.id;
        apiMention.sentiment = this.sentiment;
        apiMention.audienceReach = this.audienceReach;
        apiMention.canGenerateInsight = this.canGenerateInsight;
        apiMention.isAudienceFromSimilarWeb = this.isAudienceFromSimilarWeb;
        apiMention.isLexisNexis = this.isLexisNexis;
        apiMention.isBurrelles = this.isBurrelles;
        apiMention.isPmgXcago = this.isPmgXcago;
        apiMention.isCla = this.isCla;
        apiMention.isNla = this.isNla;
        apiMention.isFinancialTimes = this.isFinancialTimes;
        apiMention.attribution = this.attribution;
        /* @TODO
         *  These fields don't currently work, back end rejects them as unknown, will have to be changed if the edit form ever includes these fields
         *   apiMention.isRetweet = this.isRetweet;
         *  apiMention.isTweetReply = this.isTweetReply;
         */
        apiMention.adValueUsd = this.adValueUsd;
        apiMention.cityId = this.cityId ? '' + this.cityId : null;
        apiMention.continentId = this.continentId ? '' + this.continentId : null;
        apiMention.countryId = this.countryId ? '' + this.countryId : null;
        apiMention.countyId = this.countyId ? '' + this.countyId : null;
        apiMention.provinceId = this.provinceId ? '' + this.provinceId : null;
        if (this.contexualKeywordIndices) {
            apiMention.contexualKeywordIndices = this.contexualKeywordIndices.map(indices => indices.toApiModel());
        }

        if (this.summaryContextualKeywordIndices) {
            apiMention.summaryContextualKeywordIndices = this.summaryContextualKeywordIndices.map(indices => indices.toApiModel());
        }

        if (this.tagIds) {
            apiMention.tagIds = this.tagIds;
        }
        if (this.criticalMentionWordPlayUrl) {
            apiMention.criticalMentionWordPlayUrl = this.criticalMentionWordPlayUrl;
        }
        apiMention.note = this.note;
        apiMention.summary = this.summary;
        apiMention.wordCount = this.wordCount;
        apiMention.tveyesArchivedState = this.tvEyesArchiveState;
        apiMention.tvEyesInstaPlayUrl = this.tvEyesInstaPlayUrl;
        apiMention.attachmentMediaIds = this.attachments;
        apiMention.cachedUrl = this.cachedUrl;

        if (this.insights?.length) {
            apiMention.insights = [];
            this.insights.forEach(insight => apiMention.insights.push(insight.toApiModel()));
        } else {
            apiMention.insights = null;
        }

        if (apiMention.mediaType === MediaTypeEnum.TV || apiMention.mediaType === MediaTypeEnum.RADIO) {
            apiMention.clipDuration = this.duration;
        } else {
            apiMention.clipDuration  = null;
        }

        if (this.viewerUrl) {
            apiMention.viewerUrl = this.viewerUrl;
        }
        if (this.cityName) {
            apiMention.cityName = this.cityName;
        }
        if (this.stateName) {
            apiMention.stateName = this.stateName;
        }
        if (this.countryName) {
            apiMention.countryName = this.countryName;
        }
        if (this.originalUrl) {
            apiMention.originalUrl = this.originalUrl;
        }

        if (this.isTranslatedPreferred) {
            apiMention.isTranslatedPreferred = this.isTranslatedPreferred;
        } else {
            apiMention.isTranslatedPreferred = false;
        }

        apiMention.isEdited = this.isEdited;

        if (this.translation) {
            apiMention.translation = {};
            if (this.translation.headline) {
                apiMention.translation.headline = this.translation.headline;
            }
            if (this.translation.lead) {
                apiMention.translation.lead = this.translation.lead;
            }
            if (this.translation.summary) {
                apiMention.translation.summary = this.translation.summary;
            }
            if (this.translation.body) {
                apiMention.translation.body = this.translation.body;
            }
            if (this.translation.language) {
                apiMention.translation.language = this.translation.language;
            }
        }

        apiMention.socialData = new MentionSocialDataModel();
        apiMention.socialData.username = this.username;

        apiMention.socialData.tweetData = this.tweetData;
        apiMention.socialData.redditData = this.redditData;
        apiMention.socialData.youtubeData = this.youtubeData;
        apiMention.socialData.tiktokData = this.tiktokData;
        apiMention.socialData.linkedinData = this.linkedinData;
        apiMention.socialData.blueskyData = this.blueskyData;

        // handle fallback case, don't save f/e fallback value
        if (this.socialAvatarUrl?.includes('unavatar.io') && this.mediaType.id === MediaTypeEnum.REDDIT) {
            apiMention.socialData.avatarUrl = null;
        } else {
            apiMention.socialData.avatarUrl = this.socialAvatarUrl;
        }

        apiMention.socialData.engagementLastFetchedDate = this.engagementLastFetchedDate?.getTime();

        // remove 'u/' or '@'
        switch (this.mediaType.id) {
            case MediaTypeEnum.TWITTER:
            case MediaTypeEnum.TIKTOK:
            case MediaTypeEnum.INSTAGRAM:
            case MediaTypeEnum.FACEBOOK:
            case MediaTypeEnum.TIKTOK:
            case MediaTypeEnum.BLUESKY:
                apiMention.socialData.handle = this.handle?.replace(/@/,'');
                break;
            case MediaTypeEnum.REDDIT:
                apiMention.socialData.handle = this.handle?.replace(/u\/|@/,'');
                break;
            case MediaTypeEnum.LINKEDIN:
            default:
                apiMention.socialData.handle = this.handle;
                break;
        }

        apiMention.isBroadcastAdvertisement = this.isBroadcastAdvertisement;

        const engagementData = [];

        if (this.view !== null) {
            engagementData.push(
                this.setEngagementValueByType(EngagementEventTypeEnum.view, this.view, this.engagementAge)
            );
        }

        if (this.like !== null) {
            engagementData.push(
                this.setEngagementValueByType(EngagementEventTypeEnum.like, this.like, this.engagementAge)
            );
        }

        if (this.share !== null) {
            engagementData.push(
                this.setEngagementValueByType(EngagementEventTypeEnum.share, this.share, this.engagementAge)
            );
        }

        if (this.reply !== null) {
            engagementData.push(
                this.setEngagementValueByType(EngagementEventTypeEnum.reply, this.reply, this.engagementAge)
            );
        }

        if (this.quote !== null) {
            engagementData.push(
                this.setEngagementValueByType(EngagementEventTypeEnum.quote, this.quote, this.engagementAge)
            );
        }

        if (engagementData.length > 0) {
            apiMention.socialData.engagementData = engagementData;
        }

        if (Object.values(apiMention.socialData).filter(x => !!x).length === 0) {
            apiMention.socialData = null;
        }

        return apiMention;
    }


    /*
    *  Classify a sentiment value (-1 through 1) as negative, neutral or positive
    */
    public getSentimentName(value: number): string {
        let sentiment = 'Sentiment.NOT_AVAILABLE';

        if (value === null) {
            sentiment = 'Sentiment.NOT_AVAILABLE';
        } else if (value >= -1 && value <= -0.3) {
            sentiment = 'Sentiment.NEGATIVE';
        } else if (value >= -0.2 && value <= 0.2) {
            sentiment = 'Sentiment.NEUTRAL';
        } else if (value >= 0.3) {
            sentiment = 'Sentiment.POSITIVE';
        }

        return sentiment;
    }

    /**
     * Returns an api model for the update mention PUT call
    */
    public toUpdateMentionApiModel(): MentionModel {
        const updateMentionApiModel = this.convertToApiMention();
        delete updateMentionApiModel.isArchivedCriticalMention;
        delete updateMentionApiModel.images;

        return updateMentionApiModel;
    }

    /**
     * Returns an api model for the update mention translation call
    */
    public toUpdateMentionTranslationApiModel(): MentionModel {
        const apiMention: MentionModel = new MentionModel();
        apiMention.id = this.mentionId;

        if (this.isTranslatedPreferred) {
            apiMention.isTranslatedPreferred = this.isTranslatedPreferred;
        } else {
            apiMention.isTranslatedPreferred = false;
        }

        if (this.translation) {
            apiMention.translation = {};
            if (this.translation.headline) {
                apiMention.translation.headline = this.translation.headline;
            }
            if (this.translation.lead) {
                apiMention.translation.lead = this.translation.lead;
            }
            if (this.translation.summary) {
                apiMention.translation.summary = this.translation.summary;
            }
            if (this.translation.body) {
                apiMention.translation.body = this.translation.body;
            }
            if (this.translation.language) {
                apiMention.translation.language = this.translation.language;
            }
        }

        return apiMention;
    }

    /**
     * Returns an api model when only setting the preferred translation
    */
    public toUpdateMentionTranslationPreferredApiModel(): MentionModel {
        const apiMention: MentionModel = new MentionModel();
        apiMention.id = this.mentionId;

        if (this.isTranslatedPreferred) {
            apiMention.isTranslatedPreferred = this.isTranslatedPreferred;
        } else {
            apiMention.isTranslatedPreferred = false;
        }

        return apiMention;
    }

    public clone(): Mention {
        return basicClone(this);
    }

    public anonymize(): void {
        this.id = null;
        this.createdByUserId = null;
        this.createdDate = null;
        this.lastModifiedByUserId = null;
        this.lastModifiedDate = null;
    }

    /**
     * Checks if this mention contains all the same data as another mention
     */
    public isEqual(other: Mention): boolean {
        const diff = this.getPatch(other);

        return diff === null;
    }

    /**
     * Gets a reduced copy of the api model for when the user manually creates a custom mention
     */
    public getCreateModel(): CreateMentionApiModel {
        const createModel = {};
        const apiModel = this.convertToApiMention();
        for (let i = 0; i < createMentionApiProperties.length; i++) {
            const field = createMentionApiProperties[i];
            createModel[field] = apiModel[field];
        }

        return createModel as CreateMentionApiModel;
    }

    /**
     * Gets the differences between this mention and an original (unedited) copy
     */
    public getPatch(original: Mention): PartialMentionApiModel|null {
        if (this.id !== original.id || this.mentionId !== original.mentionId) {
            throw new Error('Can not patch mentions with different ids');
        }

        const self = this.convertToApiMention();
        const other = original.convertToApiMention();
        const patch: PartialMentionApiModel = {
            id: self.id
        };
        let hasChanges: boolean = false;

        // Compare simple fields
        for (let i = 0; i < this.patchSimpleCompare.length; i++) {
            const field = this.patchSimpleCompare[i];
            if (self[field] !== other[field]) {
                patch[field] = self[field];
                hasChanges = true;
            }
        }

        if (self.attachmentMediaIds.length !== other.attachmentMediaIds.length) {
            patch.attachmentMediaIds = self.attachmentMediaIds;
            hasChanges = true;
        } else if (self.attachmentMediaIds.length > 0) {
            for (let i = 0, l = self.attachmentMediaIds.length; i < l; i++) {
                if (self.attachmentMediaIds[i] !== other.attachmentMediaIds[i]) {
                    patch.attachmentMediaIds = self.attachmentMediaIds;
                    hasChanges = true;
                    break;
                }
            }
        }

        if (self.images.length !== other.images.length) {
            // there are a different number of images, so automatically there's a change
            patch.images = self.images;
            hasChanges = true;
        } else if (self.images.length > 0) {
            // check if any image has any changes to it, and patch the whole set of images if there are any differences
            for (let i = 0, l = self.images.length; i < l; i++) {
                const selfImage = self.images[i];
                const otherImage = other.images[i];

                if (selfImage.url !== otherImage.url) {
                    patch.images = self.images;
                    hasChanges = true;
                    break;
                }
                if (!this.imageMetadataIsMatch(selfImage.metadata, otherImage.metadata)) {
                    patch.images = self.images;
                    hasChanges = true;
                    break;
                }
                if (!!selfImage.isPreferred !== !!otherImage.isPreferred) {
                    patch.images = self.images;
                    hasChanges = true;
                    break;
                }
            }
        }

        // folders
        if (self.folderIds.length !== other.folderIds.length) {
            // there are a different number of folders, so automatically there's a change
            patch.folderIds = self.folderIds;
            hasChanges = true;
        } else if (self.folderIds?.length) {
            self.folderIds.forEach((item, index) => {
                if (item !== other.folderIds[index]) {
                    patch.folderIds = self.folderIds;
                    hasChanges = true;
                }
            });
        }

        if (!_isEqual(self.folderIds, other.folderIds)) {
            patch.folderIds = self.folderIds;
            hasChanges = true;
        }
        if (!_isEqual(self.tagIds, other.tagIds)) {
            patch.tagIds = self.tagIds;
            hasChanges = true;
        }

        if (!_isEqual(self.translation, other.translation)) {
            patch.translation = cloneDeep(self.translation);
            hasChanges = true;
        }

        if (!_isEqual(self.isTranslatedPreferred, other.isTranslatedPreferred)) {
            patch.isTranslatedPreferred = self.isTranslatedPreferred;
            hasChanges = true;
        }

        if (!_isEqual(self.socialData, other.socialData)) {
            patch.socialData = self.socialData;
            hasChanges = true;
        }

        if (!hasChanges) {
            return null;
        }

        if ('audienceReach' in patch) {
            // if the user customized the audience reach, then it's value is no longer from similar web
            patch.isAudienceFromSimilarWeb = false;
        }

        if ('contactName' in patch && !('contactId' in patch)) {
            // if the user customized the contact name, but didn't match to another contact, then the id is no longer valid
            patch.contactId = null;
        }

        if ('outletName' in patch && !('outletId' in patch)) {
            // if the user customized the outlet name, but didn't match to another outlet, then the id is no longer valid
            patch.outletId = null;
        }

        return patch;
    }

    /**
     * Gets an embeddable url for a social mention
     */
    getEmbedURL(): string {
        if (!this.mediaType.isSocial || !this.url) {
            return null;
        }

        switch (this.mediaType.id) {
            case MediaTypeEnum.YOUTUBE:
                if (webVideoHelper.isYoutubeURL(this.url)) {
                    return webVideoHelper.getYoutubeEmbedURL(this.url);
                }
                break;


            // @TODO: NEP-10761 uncomment when we get the full approval for adding reddit to monitoring mentions view
            // case MediaTypeEnum.REDDIT:
            //     return `<blockquote class="reddit-embed-bq" style="height:500px" data-embed-height="500">
            //         <a href="${this.url}">Reddit Post</a>
            //         </blockquote>
            //         <script async src="https://embed.reddit.com/widgets.js" charset="UTF-8"></script>`;
            case MediaTypeEnum.FACEBOOK:
                let url = this.url;

                // current facebook url looks like https://www.facebook.com/####_####
                // we just want to replace the "_" to "/posts"
                // this regex checks for facebook.com/{anyNumber}_{anyNumber} and NOTHING after it
                // $ matches the end of the string, so if there's anything after the {anyNumber} it will fail
                const slMatch = new RegExp('(facebook.com\/([0-9])*(_)([0-9])*)$', 'gs');

                if (this.url.match(slMatch)) {
                    url = this.url.replace('_', '/posts/');
                }
                return `https://www.facebook.com/plugins/post.php?href=${encodeURIComponent(url)}&width=350`;

            case MediaTypeEnum.LINKEDIN:
                const regex = /https:\/\/www\.linkedin\.com\/feed\/update\/(urn:li:activity:\d+)/;
                const match = this.url.match(regex);
                if (match && match[1]) {
                    return `https://www.linkedin.com/embed/feed/update/${match[1]}`;
                }
                break;
        }

        return null;
    }

    /**
     * Precision Clipping can only be performed on mentions that;
     * - are of media type TV or Radio
     * - have a valid tvEyes url's
     * - media content has not been edited
     */
    isPrecisionClipable(): boolean {
        if (!this.url) {
            return false;
        }

        const parsedUrl = new URL(this.url);

        const unedited: boolean = ![
            TVEyesArchivedStateEnum.EDITED,
            TVEyesArchivedStateEnum.COMPLETED,
            TVEyesArchivedStateEnum.PROCESSING
        ].includes(this.tvEyesArchiveState);

        if (
            [MediaTypeEnum.TV, MediaTypeEnum.RADIO].includes(this.mediaType.id) &&
            parsedUrl.hostname.endsWith('.tveyes.com') &&
            unedited
        ) {
            return true;
        }

        return false;
    }


    /**
     * Checks if a mention source is TV Eyes
     */
    isTvEyes(): boolean {
        if (this.tvEyesPodcastID) {
            return true;
        }
        if (
            !this.url
            || (
                this.mediaType.id !== MediaTypeEnum.TV
                && this.mediaType.id !== MediaTypeEnum.RADIO
            )
        ) {
            return false;
        }

        const parsedUrl = new URL(this.url);

        return parsedUrl.hostname.endsWith('.tveyes.com');
    }

    /**
     * Gets the podcast id, with backward compatibility for before the id was given it's own property
     */
    private getPodcastId(apiModel: MentionModel): string {
        // Not a podcast, so ignore it
        if (apiModel.mediaType !== MediaTypeEnum.PODCAST) {
            return null;
        }

        // ID is stored on property, so use that
        if (apiModel.mediaPlayerId) {
            return apiModel.mediaPlayerId;
        }

        // MercuryId exists, so try that
        if (apiModel.mercuryId) {
            const match = apiModel.mercuryId.match(/^Podcast\|\d+\|(.{27})_/i);

            if (match) {
                return match[1];
            }
        }

        return null;
    }

    /**
     * Gets the images from the provided API entity
    */
    private getImagesFromApiEntity(apiEntity: MentionModel): ImageModel[] {
        const hasApiImages: boolean = Array.isArray(apiEntity.images) && apiEntity.images.length > 0;
        const images: ImageModel[] = [];

        if (hasApiImages) {
            apiEntity.images
                .sort((a, b) => a.url?.localeCompare(b.url))
                .forEach(
                    image => {
                        const newImage: ImageModel = new ImageModel(image);
                        images.push(newImage);
                    }
                );

            // If there is no preferred image, the first one is set as default.
            const hasPreferredImage = images.some(img => img.isPreferred);
            if (!hasPreferredImage) {
                images[0].isPreferred = true;
            }
        }

        return images;
    }

    /**
     * Verifies that 2 images share the same metadata
     */
    private imageMetadataIsMatch(a: ImageMetadataApiModel[], b: ImageMetadataApiModel[]): boolean {
        for (let i = 0, l = a.length; i < l; i++) {
            const aMeta = a[i];
            const bMeta = b[i];

            if (aMeta.type !== bMeta.type || aMeta.description !== bMeta.description) {
                return false;
            }
        }

        return true;
    }

    /**
     * Creates a Date object from a variety of sources
     * @HACK this exists because for some reason you can't just pass these things into
     *      the Date constructor directly or TypeScript will complain
     *
     * @param dateLike Something that can be converted to a Date
     *
     * @returns A proper Date object
     */
    private convertDate(dateLike: number | string | Date): Date {
        if (typeof dateLike === 'number') {
            return new Date(+dateLike);
        } else if (typeof dateLike === 'string') {
            return new Date('' + dateLike);
        } else if (dateLike instanceof Date) {
            return dateLike as Date;
        }
        return null;
    }

    /**
     * Set up mention sharing information for quick sharing.
     */
    setMentionSharing(): void {
        this.shareable = true;
        this.sharingInformation = new ContentSharingModel();

        // We are choosing to prevent the ability to share certain mentions:
        //  - Mentions without a URL
        //  - Mentions that are radio or TV
        //  - Mentions that are from NLA
        if (
            !this.url ||
            this.mediaType.id === MediaTypeEnum.RADIO ||
            this.mediaType.id === MediaTypeEnum.TV ||
            this.url.indexOf('www.nla-eclips.com') !== -1 ||
            this.isPmgXcago ||
            this.isLexisNexis
        ) {
            this.shareable = false;
            return;
        }
        // Burrelles print content can only be shared through email
        // Only user create print mentions can have social media searching, user create mentions do NOT have a mercury id
        if (this.mediaType.id !== MediaTypeEnum.PRINT ||
            (!this.isBurrelles && !this.id)) {
            const tweetText = this.buildSharingHeadline();
            this.sharingInformation.tweet = new ContentSharingTweetModel(tweetText, this.url);

            this.sharingInformation.facebook = new ContentSharingFacebookModel(this.url);
            this.sharingInformation.linkedin = new ContentSharingLinkedinModel(this.url);
        }

        const emailSubject = this.buildSharingHeadline();
        this.sharingInformation.email = new ContentSharingEmailModel(emailSubject, this.url);
    }

    /**
     * Builds a string using the headline of the mention and the outlet of the mention in the form of:
     *     headline | outlet
     *
     * @returns {string}
     */
    buildSharingHeadline(): string {
        let sharingHeadline = this.headline;

        if (this.publication.name) {
            sharingHeadline = sharingHeadline + ' | ' + this.publication.name;
        }
        if (!sharingHeadline) {
            sharingHeadline = '';
        }
        return sharingHeadline;
    }

    getTVEyesDisplayUrl(): string {
        if (this.tvEyesArchiveState === TVEyesArchivedStateEnum.COMPLETED && this.viewerUrl) {
            return this.viewerUrl;
        } else if (this.tvEyesInstaPlayUrl) {
            return this.tvEyesInstaPlayUrl;
        }
        return null;
    }

    private getEngagementValueByType(engagementEvent: EngagementEventTypeEnum, engagementData: EngagementDataModel[]): number {
        const engagement = engagementData?.find(item => item.type === engagementEvent);
        let result = null;
        engagement?.engagementValues?.forEach(value => {
            if (value.count || value.count === 0) {
                result = value.count;
            }
        });

        return result;
    }

    /**
     * Find the first engagement age value, since they all should be the same
     */
    private getEngagementAge(engagementData: EngagementDataModel[]): EngagementAgeEnum {
        if (!engagementData) {
            return null;
        }

        let age: EngagementAgeEnum;
        for(const engagement of engagementData) {
            for (const value of engagement?.engagementValues) {
                if (value.age) {
                    age = value.age;
                    break;
                }
            }
        }

        return age;
    }

    private setEngagementValueByType(
        engagementType: EngagementEventTypeEnum,
        value: number,
        age: EngagementAgeEnum
    ): EngagementDataModel {

        const dataModel: EngagementValue = {
            age: age || EngagementAgeEnum.latest,
            count: value
        };
        const data = new EngagementDataModel(engagementType, [dataModel]);

        return data;
    }

    private processHandle(handle: string): string {
        let res: string = handle;
        switch (this.mediaType.id) {
            case MediaTypeEnum.TWITTER:
            case MediaTypeEnum.TIKTOK:
            case MediaTypeEnum.INSTAGRAM:
            case MediaTypeEnum.FACEBOOK:
            case MediaTypeEnum.TIKTOK:
            case MediaTypeEnum.BLUESKY:
                res = '@' + res;
                break;
            case MediaTypeEnum.REDDIT:
                res = 'u/' + res;
                break;
        }

        return res;
    }

    private getAvatarPlaceholderText(handle: string): string {
        let placeholderText: string;
        switch (this.mediaType.id) {
            case MediaTypeEnum.TWITTER:
                placeholderText = 'X';
                break;
            case MediaTypeEnum.TIKTOK:
                placeholderText = 'T';
                break;
            case MediaTypeEnum.INSTAGRAM:
                placeholderText = 'I';
                break;
            case MediaTypeEnum.FACEBOOK:
                placeholderText = 'F';
                break;
            case MediaTypeEnum.REDDIT:
                placeholderText = 'R';
                break;
            case MediaTypeEnum.BLUESKY:
                placeholderText = 'B';
                break;
            case MediaTypeEnum.YOUTUBE:
                placeholderText = 'Y';

                if (this.author?.name) {
                    placeholderText = this.author?.name.charAt(0).toUpperCase();
                }
                break;
        }

        if (handle && this.mediaType.id !== MediaTypeEnum.YOUTUBE) {
            placeholderText = handle.charAt(0).toUpperCase();
        }

        if (this.tweetData?.type === TwitterSubTypeEnum.RETWEET && this.tweetData?.originalTweetData?.originalUserHandle) {
            placeholderText = this.tweetData.originalTweetData.originalUserHandle.charAt(0).toUpperCase();
        }

        if (this.linkedinData?.type === LinkedinSubTypeEnum.REPOST && this.linkedinData?.originalActivityData?.originalUserHandle) {
            placeholderText = this.linkedinData?.originalActivityData?.originalUserHandle.charAt(0).toUpperCase();
        }

        return placeholderText;
    }

    private prepareSocialUrl(socialData?: MentionSocialDataModel): void {
        const handle = this.handle?.substring(1);

        if (!handle && !this.subreddit && !(socialData?.youtubeData?.channelUrl)) {
            return;
        }

        switch (this.mediaType.id) {
            case MediaTypeEnum.TWITTER:
                if (this.tweetData?.type !== TwitterSubTypeEnum.RETWEET) {
                    this.socialUrl = 'https://twitter.com/' + handle;
                } else {
                    if (this.tweetData?.originalTweetData?.originalUserHandle) {
                        this.socialUrl = 'https://twitter.com/' + this.tweetData.originalTweetData.originalUserHandle?.substring(1);
                    }
                }
                break;
            case MediaTypeEnum.REDDIT:
                this.socialUrl = 'https://www.reddit.com/' + this.subreddit;
                break;
            case MediaTypeEnum.FACEBOOK:
                this.socialUrl = 'https://www.facebook.com/' + handle;
                break;
            case MediaTypeEnum.INSTAGRAM:
                this.socialUrl = 'https://www.instagram.com/' + handle;
                break;
            case MediaTypeEnum.BLUESKY:
                this.socialUrl = 'https://bsky.app/profile/' + handle;
                break;
            case MediaTypeEnum.YOUTUBE:
                this.socialUrl = socialData?.youtubeData?.channelUrl;
                break;
            case MediaTypeEnum.TIKTOK:
                this.socialUrl = 'https://www.tiktok.com/@' + handle;
                break;
            case MediaTypeEnum.LINKEDIN:
                let profileType = this.linkedinData?.handlePageType;
                let linkedInHandle = this.handle;
                if (this.linkedinData?.type === LinkedinSubTypeEnum.REPOST) {
                    profileType = this.linkedinData.originalActivityData?.handlePageType;
                    linkedInHandle = this.linkedinData.originalActivityData?.originalUserHandle;
                }
                if (profileType && linkedInHandle) {
                    switch (profileType) {
                        case HandlePageTypeEnum.COMPANY:
                            this.socialUrl = 'https://www.linkedin.com/company/' + linkedInHandle;
                            break;
                        case HandlePageTypeEnum.PERSONAL:
                            this.socialUrl = 'https://www.linkedin.com/in/' + linkedInHandle;
                            break;
                        case HandlePageTypeEnum.SCHOOL:
                            this.socialUrl = 'https://www.linkedin.com/school/' + linkedInHandle;
                            break;
                        case HandlePageTypeEnum.SHOWCASE:
                            this.socialUrl = 'https://www.linkedin.com/showcase/' + linkedInHandle;
                            break;
                    }
                }
                break;
        }
    }

    private isMentionArchived(mention: MentionModel): boolean {
        return (
            mention.isArchivedCriticalMention
            || [TVEyesArchivedStateEnum.COMPLETED, TVEyesArchivedStateEnum.PROCESSING, TVEyesArchivedStateEnum.EDITED].includes(mention.tveyesArchivedState)
        );
    }
}
